import { useState, useEffect, useMemo } from 'react';
import { Drawer, DrawerProps } from 'antd';
import { DrawerCloseSvg } from '@/sub-luca-plugin/compatible-components/svg-icons';
import { useNewestData } from '@/sub-luca-plugin/utils/custom-hooks';
import { queryDomExample } from '@/sub-luca-plugin/utils/class-example';
import { useGlobalStore } from '@/sub-luca-plugin/store/global';
import { preventBodyScroll, recoverBodyScroll } from '@/sub-luca-plugin/utils/tools';
import { t, tCommon } from '@/sub-luca-plugin/i18n';
import classNames from 'classnames';
import './index.scss';

const DragDrawer = (props: DrawerProps) => {
    const { open, children, onClose } = props;
    const { dragDrawerWidth, setDragDrawerWidth, dragDrawerMaxWidth, dragDrawerMinWidth } = useGlobalStore();
    const newestWidth = useNewestData(dragDrawerWidth);
    const [dragTipsStepNum, setDragTipsStepNum] = useState<number | undefined>(() => {
        return localStorage.getItem('dragDrawerWidth') ? undefined : 1;
    });

    const handleMouseDown = () => {
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        const wrapper = queryDomExample.querySelector('.ant-drawer-content-wrapper');
        wrapper.style.transition = 'none';
        const drawerBody = queryDomExample.querySelector('.ant-drawer-body');
        drawerBody.className += ' disabled-select';
    };

    const handleMouseUp = () => {
        localStorage.setItem('dragDrawerWidth', newestWidth.current + '');
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        const wrapper = queryDomExample.querySelector('.ant-drawer-content-wrapper');
        wrapper.style.transition = 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)';
        const drawerBody = queryDomExample.querySelector('.ant-drawer-body');
        drawerBody.className = drawerBody.className.replace(' disabled-select', '');
    };

    const handleMouseMove = (ev: any) => {
        let currentWidth = document.body.clientWidth - ev.pageX;
        if (currentWidth === newestWidth.current) return;
        if (currentWidth < dragDrawerMinWidth) currentWidth = dragDrawerMinWidth;
        if (currentWidth > dragDrawerMaxWidth) currentWidth = dragDrawerMaxWidth;
        setDragDrawerWidth(currentWidth);
    };

    const handleViewNextTips = () => {
        if (dragTipsStepNum === 1) {
            setDragTipsStepNum(2);
        } else if (dragTipsStepNum === 2) {
            setDragTipsStepNum(undefined);
            localStorage.setItem('dragDrawerWidth', newestWidth.current + '');
        }
    };

    useEffect(() => {
        const dragHandleLine = queryDomExample.querySelector('.drag-drawer__handle-line');
        if (!open || dragTipsStepNum !== undefined)
            return () => {
                dragHandleLine?.removeEventListener('mousedown', handleMouseDown);
            };
        if (dragHandleLine) {
            dragHandleLine.addEventListener('mousedown', handleMouseDown);
        }
    }, [open, dragTipsStepNum]);

    useEffect(() => {
        open ? preventBodyScroll() : recoverBodyScroll();
    }, [open]);

    const DragDrawer = useMemo(() => {
        return dragTipsStepNum === undefined ? (
            <div className="drag-drawer__handle-line" />
        ) : (
            <>
                <div className="drag-drawer__handle-overlay" />
                <div className={classNames('drag-drawer__tips-handle', dragTipsStepNum === 2 && 'drag-drawer__tips-handle--two-step')}>
                    <div className="drag-drawer__tips-handle-arrow"></div>
                    <div className="drag-drawer__tips-handle-container">
                        <div className="drag-drawer__tips-handle-content">
                            {dragTipsStepNum === 1
                                ? tCommon.t('dragGuide.step1Hint', '鼠标悬浮至页面左边，显示拖拽功能')
                                : tCommon.t('dragGuide.step2Hint', '点击拖动即可拖拽页面宽度')}
                        </div>
                        <div className="drag-drawer__tips-handle-next" onClick={handleViewNextTips}>
                            {dragTipsStepNum === 1
                                ? tCommon.t('dragGuide.nextButton', '下一步（1/2）')
                                : tCommon.t('dragGuide.confirmButton', '我知道了')}
                        </div>
                    </div>
                </div>
            </>
        );
    }, [dragTipsStepNum]);

    return (
        <Drawer
            className="drag-drawer"
            styles={{ header: { display: 'none' }, body: { overflow: 'unset', padding: 0, position: 'relative' }, mask: { opacity: 0 } }}
            onClose={onClose}
            open={open}
            // destroyOnHidden
            keyboard={false}
            {...props}
            width={dragDrawerWidth}
            getContainer={queryDomExample.queryLucaQaRoot}
        >
            {DragDrawer}
            <DrawerCloseSvg onClick={onClose} className="drag-drawer__close" />
            {children}
        </Drawer>
    );
};

export default DragDrawer;
